/**
 * Credential management panel: list, add, and delete credentials.
 */

import { useState } from 'react';
import { Plus, Trash2, Key } from 'lucide-react';
import { useCredentials, useCreateCredential, useDeleteCredential } from '@/hooks/useCredentials';
import CredentialForm from './CredentialForm';
import type { CredentialCreateRequest, CredentialType } from '@/types/credential';
import { formatDate } from '@/utils/formatters';

const TYPE_LABELS: Record<CredentialType, string> = {
  ssh_password: 'SSH Password',
  ssh_key: 'SSH Key',
  winrm_password: 'WinRM',
  snmp: 'SNMP v1/v2c',
  snmpv3: 'SNMPv3',
};

const TYPE_COLORS: Record<CredentialType, string> = {
  ssh_password: 'bg-blue-100 text-blue-700',
  ssh_key: 'bg-purple-100 text-purple-700',
  winrm_password: 'bg-green-100 text-green-700',
  snmp: 'bg-orange-100 text-orange-700',
  snmpv3: 'bg-amber-100 text-amber-700',
};

export default function CredentialManager() {
  const { data: credentials = [], isLoading } = useCredentials();
  const createMutation = useCreateCredential();
  const deleteMutation = useDeleteCredential();
  const [showForm, setShowForm] = useState(false);

  function handleCreate(payload: CredentialCreateRequest) {
    createMutation.mutate(payload, {
      onSuccess: () => setShowForm(false),
    });
  }

  function handleDelete(id: string, name: string) {
    if (confirm(`Delete credential "${name}"? This will detach it from all assets.`)) {
      deleteMutation.mutate(id);
    }
  }

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading credentials…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Scan Credentials</h2>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add credential
          </button>
        )}
      </div>

      {showForm && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">New credential</h3>
          <CredentialForm
            onSubmit={handleCreate}
            onCancel={() => setShowForm(false)}
            isSubmitting={createMutation.isPending}
          />
          {createMutation.isError && (
            <p className="mt-2 text-sm text-red-600">
              Failed to save credential. Please try again.
            </p>
          )}
        </div>
      )}

      {credentials.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
          <Key className="h-8 w-8 text-gray-400" />
          <p className="text-sm text-gray-500">
            No credentials yet. Add SSH, WinRM, or SNMP credentials to enable authenticated scanning.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Username</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {credentials.map((cred) => (
                <tr key={cred.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                    {cred.name}
                    {cred.description && (
                      <span className="ml-2 text-xs text-gray-400">{cred.description}</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_COLORS[cred.credential_type]}`}>
                      {TYPE_LABELS[cred.credential_type]}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-gray-700">
                    {cred.credential_type === 'snmp' ? '— community string —' : cred.username}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                    {formatDate(cred.created_at)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(cred.id, cred.name)}
                      disabled={deleteMutation.isPending}
                      className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                      title="Delete credential"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
